/**
 * GameLoop - Fixed timestep physics/logic update (60hz) with decoupled rendering.
 */

export class GameLoop {
  private isRunning = false;
  private animFrameId: number | null = null;
  private lastTime = 0;
  private accumulator = 0;
  private readonly fixedStep = 1 / 60; // 60 updates per second

  private updateFn: (dt: number) => void;
  private renderFn: (interp: number) => void;

  constructor(
    updateFn: (dt: number) => void,
    renderFn: (interp: number) => void
  ) {
    this.updateFn = updateFn;
    this.renderFn = renderFn;
  }

  public start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.loop = this.loop.bind(this);
    this.animFrameId = requestAnimationFrame(this.loop);
  }

  public stop(): void {
    this.isRunning = false;
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
  }

  private loop(currentTime: number): void {
    if (!this.isRunning) return;

    let frameTime = (currentTime - this.lastTime) / 1000;
    if (frameTime > 0.25) frameTime = 0.25; // Clamp spiral of death
    this.lastTime = currentTime;

    this.accumulator += frameTime;

    while (this.accumulator >= this.fixedStep) {
      this.updateFn(this.fixedStep);
      this.accumulator -= this.fixedStep;
    }

    const interp = this.accumulator / this.fixedStep;
    this.renderFn(interp);

    this.animFrameId = requestAnimationFrame(this.loop);
  }
}
